import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { useSearchParams } from "react-router-dom";
import { trackShare, trackWhatsAppLead, trackCountdownView } from "../analytics";
import {
  Box, Typography, Button, Stack, Chip, TextField,
} from "@mui/material";
import {
  WhatsApp, Share, ArrowForward, ContentCopy, Check,
  NotificationsActive, Timer,
} from "@mui/icons-material";

const DOMAIN  = "https://drpawleb.com";
const DISPLAY = '"Cormorant Garamond", Georgia, serif';
const BLUE    = "#1565C0";
const BABY    = "#42A5F5";

const DrPawLogo: React.FC = () => (
  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
    <Box sx={{
      width: 42, height: 42, borderRadius: "12px",
      background: "linear-gradient(135deg,#1565C0,#42A5F5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 16px rgba(21,101,192,.35)",
    }}>
      <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "1.05rem" }}>D</Typography>
    </Box>
    <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: "1.65rem", letterSpacing: "-.01em", fontFamily: DISPLAY }}>
      Dr.Paw
    </Typography>
  </Stack>
);

const pad = (n: number) => String(Math.max(0, n)).padStart(2, "0");

// ─────────────────────────────────────────────────────────────────────────────
const PublicCountdown: React.FC = () => {
  const [searchParams] = useSearchParams();
  const title    = decodeURIComponent(searchParams.get("title")    || "");
  const subtitle = decodeURIComponent(searchParams.get("subtitle") || "");
  const emoji    = decodeURIComponent(searchParams.get("emoji")    || "🎓");
  const badge    = decodeURIComponent(searchParams.get("badge")    || "");
  const cta      = decodeURIComponent(searchParams.get("cta")      || "Secure My Spot");
  const link     = searchParams.get("link")  || `${DOMAIN}/#pricing`;
  const until    = parseInt(searchParams.get("until") || "0", 10);

  const [time,     setTime]     = useState({ d: 0, h: 0, m: 0, s: 0 });
  const [ended,    setEnded]    = useState(false);
  const [waName,   setWaName]   = useState("");
  const [waPhone,  setWaPhone]  = useState("");
  const [notified, setNotified] = useState(false);
  const [copied,   setCopied]   = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { if (title) trackCountdownView(title); }, [title]);

  useEffect(() => {
    if (!until) return;
    const calc = () => {
      const diff = until - Date.now();
      if (diff <= 0) { setEnded(true); setTime({ d: 0, h: 0, m: 0, s: 0 }); clearInterval(intervalRef.current!); return; }
      setTime({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    calc();
    intervalRef.current = setInterval(calc, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [until]);

  const elapsed      = until ? Math.max(0, until - Date.now()) : 0;
  const maxDuration  = 30 * 24 * 3600 * 1000;
  const progressPct  = until ? Math.max(0, Math.min(100, 100 - (elapsed / maxDuration) * 100)) : 0;

  const handleNotify = () => {
    if (!waPhone.trim()) return;
    trackWhatsAppLead(title);
    const msg = encodeURIComponent(
      `Hi Dr.Paw! I want to be notified when "${title}" opens for enrollment${waName ? ` — I'm ${waName}` : ""}.`
    );
    window.open(`https://wa.me/96170886078?text=${msg}`, "_blank");
    setNotified(true);
  };

  const handleShare = async () => {
    trackShare("countdown", title);
    const d = { title: `Dr.Paw — ${title}`, text: `${emoji} "${title}" is launching soon on Dr.Paw! Don't miss it:`, url: window.location.href };
    if (typeof navigator.share === "function") { try { await navigator.share(d); return; } catch {} }
    navigator.clipboard.writeText(window.location.href);
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  };

  const waShare = encodeURIComponent(`${emoji} "${title}" is launching soon on Dr.Paw! I'm already signed up — are you? 🔥\n\nDon't miss it: ${window.location.href}`);

  if (!title) return (
    <Box sx={{ minHeight: "100vh", background: "#F8F3EE", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ color: "#90A4AE" }}>Invalid countdown link.</Typography>
    </Box>
  );

  const inputSx = {
    flex: 1,
    "& .MuiOutlinedInput-root": { color: "#37474F", borderRadius: "10px", "& fieldset": { borderColor: "rgba(21,101,192,.2)" }, "&:hover fieldset": { borderColor: BLUE }, "&.Mui-focused fieldset": { borderColor: BLUE } },
    "& .MuiInputBase-input::placeholder": { color: "#90A4AE" },
  };

  const TimeBlock = ({ value, label }: { value: number; label: string }) => (
    <Box sx={{ textAlign: "center", flex: 1 }}>
      <Box sx={{
        background: "#fff", border: "1px solid rgba(21,101,192,.15)",
        borderRadius: "18px", px: { xs: 1, sm: 2 }, py: { xs: 2, sm: 2.5 },
        position: "relative", overflow: "hidden",
        boxShadow: "0 4px 16px rgba(21,101,192,.07)",
        "&::after": {
          content: '""', position: "absolute", bottom: 0, left: 0, right: 0,
          height: 3, background: `linear-gradient(90deg,transparent,${BLUE},transparent)`,
        },
      }}>
        <Typography sx={{
          color: "#0A1628", fontWeight: 700, lineHeight: 1,
          fontSize: { xs: "2.4rem", sm: "3.2rem", md: "4rem" },
          fontFamily: DISPLAY,
        }}>
          {pad(value)}
        </Typography>
      </Box>
      <Typography sx={{ color: "#546E7A", fontSize: ".7rem", fontWeight: 700, letterSpacing: ".15em", textTransform: "uppercase", mt: 1 }}>
        {label}
      </Typography>
    </Box>
  );

  const metaTitle = title ? `${title} — Dr.Paw` : "Course Launching Soon — Dr.Paw";
  const metaDesc  = subtitle
    ? `${subtitle}. ${cta} before it's too late. Dr.Paw veterinary education.`
    : `Enrollment opens soon. ${cta} and be the first in — Dr.Paw veterinary education.`;

  return (
    <>
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content="https://drpawleb.com/og-image.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
    </Helmet>
    <HreflangTags />
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(160deg,#FEF9F5 0%,#F8F3EE 55%,#F2EDE6 100%)",
      display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
    }}>
      <Box sx={{ position: "absolute", top: -120, right: -120, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(21,101,192,.07) 0%,transparent 70%)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle,rgba(66,165,245,.06) 0%,transparent 70%)", pointerEvents: "none" }} />

      {/* Header */}
      <Box sx={{ pt: 4, pb: 2.5, px: 3, textAlign: "center" }}>
        <DrPawLogo />
        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: BABY, fontWeight: 600, fontSize: ".7rem", letterSpacing: ".15em", textTransform: "uppercase" }}>
          Upcoming Launch
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 580, textAlign: "center" }}>

          {/* Emoji + badges */}
          <Typography sx={{ fontSize: { xs: "4rem", md: "5rem" }, lineHeight: 1, mb: 2 }}>{emoji}</Typography>
          <Stack direction="row" justifyContent="center" flexWrap="wrap" gap={1} sx={{ mb: 2.5 }}>
            <Chip
              icon={<Timer sx={{ fontSize: ".9rem !important", color: `${BLUE} !important` }} />}
              label={ended ? "Enrollment is open!" : "Coming soon"}
              sx={{ bgcolor: "rgba(21,101,192,.1)", color: BLUE, fontWeight: 700, fontSize: ".75rem", border: "1px solid rgba(21,101,192,.25)" }}
            />
            {badge && <Chip label={badge} sx={{ bgcolor: "rgba(66,165,245,.1)", color: BLUE, fontWeight: 700, fontSize: ".75rem", border: "1px solid rgba(66,165,245,.3)" }} />}
          </Stack>

          {/* Title */}
          <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: { xs: "1.9rem", md: "2.8rem" }, lineHeight: 1.15, letterSpacing: "-.01em", mb: 1.5, fontFamily: DISPLAY }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: "#546E7A", fontSize: ".95rem", lineHeight: 1.75, mb: 4, maxWidth: 420, mx: "auto" }}>
              {subtitle}
            </Typography>
          )}

          {/* Countdown blocks */}
          {!ended ? (
            <>
              <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 3, px: { xs: 0, sm: 2 } }}>
                <TimeBlock value={time.d} label="Days" />
                <TimeBlock value={time.h} label="Hours" />
                <TimeBlock value={time.m} label="Minutes" />
                <TimeBlock value={time.s} label="Seconds" />
              </Stack>

              {/* Progress urgency bar */}
              <Box sx={{ mb: 4, px: 1 }}>
                <Box sx={{ height: 6, borderRadius: 99, bgcolor: "rgba(21,101,192,.1)", overflow: "hidden", mb: 1 }}>
                  <Box sx={{ height: "100%", width: `${progressPct}%`, background: "linear-gradient(90deg,#1565C0,#42A5F5)", borderRadius: 99, transition: "width 1s ease" }} />
                </Box>
                <Typography sx={{ color: "#90A4AE", fontSize: ".75rem" }}>
                  Enrollment window closing — limited spots available
                </Typography>
              </Box>

              {/* WhatsApp notification capture */}
              {!notified ? (
                <Box sx={{ background: "#fff", border: "1px solid rgba(21,101,192,.12)", borderRadius: "24px", boxShadow: "0 4px 16px rgba(21,101,192,.06)", p: { xs: 2.5, md: 3.5 }, mb: 3, textAlign: "left" }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: "rgba(37,211,102,.1)", border: "1px solid rgba(37,211,102,.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <NotificationsActive sx={{ color: "#25D366", fontSize: "1.1rem" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: "#0A1628", fontWeight: 700, fontSize: ".9rem", fontFamily: DISPLAY }}>
                        Notify me when enrollment opens
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#546E7A" }}>
                        Get a WhatsApp message the moment spots open
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <TextField
                        placeholder="Your name" value={waName} onChange={(e) => setWaName(e.target.value)}
                        size="small" sx={inputSx}
                        inputProps={{ style: { color: "#37474F" } }}
                      />
                      <TextField
                        placeholder="+961 XX XXX XXX" value={waPhone} onChange={(e) => setWaPhone(e.target.value)}
                        size="small" sx={{ ...inputSx, flex: 1.5 }}
                        inputProps={{ style: { color: "#37474F" } }}
                      />
                    </Box>
                    <Button
                      variant="contained" startIcon={<WhatsApp />} onClick={handleNotify} fullWidth disabled={!waPhone.trim()}
                      sx={{ py: 1.4, borderRadius: "12px", textTransform: "none", fontWeight: 700, bgcolor: "#25D366", "&:hover": { bgcolor: "#1DAE56" }, "&.Mui-disabled": { opacity: .35, color: "#fff", bgcolor: "#25D366" } }}
                    >
                      Notify Me on WhatsApp
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ background: "rgba(37,211,102,.08)", border: "1px solid rgba(37,211,102,.25)", borderRadius: "18px", p: 3, mb: 3, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "2.5rem", mb: 1 }}>✅</Typography>
                  <Typography sx={{ color: "#059669", fontWeight: 700, fontSize: "1.05rem", fontFamily: DISPLAY }}>
                    You're on the list{waName ? `, ${waName}` : ""}!
                  </Typography>
                  <Typography sx={{ color: "#546E7A", fontSize: ".85rem", mt: 0.5 }}>
                    We'll WhatsApp you the moment enrollment opens.
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ mb: 4 }}>
              <Typography sx={{ color: "#059669", fontWeight: 700, fontSize: "1.1rem", mb: 2, fontFamily: DISPLAY }}>
                🎉 Enrollment is now OPEN!
              </Typography>
              <Button
                variant="contained" href={link} endIcon={<ArrowForward />} size="large"
                sx={{ px: 5, py: 1.8, borderRadius: "50px", textTransform: "none", fontWeight: 800, fontSize: "1rem", background: "linear-gradient(135deg,#1565C0,#42A5F5)", boxShadow: "0 8px 28px rgba(21,101,192,.35)", "&:hover": { transform: "translateY(-3px)" }, transition: "all .2s ease" }}
              >
                {cta}
              </Button>
            </Box>
          )}

          {/* Share section */}
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Button
              variant="contained" startIcon={<Share />} onClick={handleShare} fullWidth
              sx={{ py: 1.4, borderRadius: "50px", textTransform: "none", fontWeight: 700, background: "linear-gradient(135deg,#7C3AED,#A855F7)", boxShadow: "0 4px 14px rgba(124,58,237,.25)", "&:hover": { background: "linear-gradient(135deg,#6D28D9,#7C3AED)", transform: "translateY(-1px)" }, transition: "all .2s ease" }}
            >
              Share This Launch
            </Button>
            <Button
              variant="outlined" startIcon={<WhatsApp />} href={`https://wa.me/?text=${waShare}`} target="_blank" fullWidth
              sx={{ py: 1.3, borderRadius: "50px", textTransform: "none", fontWeight: 700, borderColor: "#25D366", color: "#25D366", "&:hover": { bgcolor: "rgba(37,211,102,.08)" } }}
            >
              Tell a Colleague on WhatsApp
            </Button>
          </Stack>

          <Button
            variant="outlined" startIcon={copied ? <Check /> : <ContentCopy />} onClick={handleCopy} fullWidth
            sx={{ py: 1.2, borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: copied ? "rgba(34,197,94,.4)" : "rgba(21,101,192,.25)", color: copied ? "#22C55E" : "#78909C", bgcolor: "#fff", transition: "all .2s ease" }}
          >
            {copied ? "Link Copied!" : "Copy Link"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ py: 3, textAlign: "center", borderTop: "1px solid rgba(21,101,192,.1)" }}>
        <Typography variant="caption" sx={{ color: "#B0BEC5", fontSize: ".72rem" }}>
          © Dr.Paw — Empowering Veterinary Professionals Worldwide
        </Typography>
      </Box>
    </Box>
    </>
  );
};

export default PublicCountdown;
